/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {connect} from 'react-redux';
import {format} from 'date-fns';
import {useNavigate} from 'react-router-dom';
import {question} from '../../assets/images/images';
import {
  DashboardLayout,
  PageLimit,
  Pagination,
  PrimaryButton,
  TableLayout,
  Loader,
  EmptyState,
  SecondaryButton,
} from '../../components';
import * as Actions from '../../store/actions';
import {
  errorCodeColor,
  filterOutEmptyValues,
  parseUrlParams,
} from '../../helpers';
import {renderServices} from '../../helpers/renderServicies';
import {PopOver} from '../../components/PopOver';
import {toast} from 'react-toastify';
import {useQuery} from 'react-query';
import {fetchApiLogsFilters} from '../../requests/queries/dashboard';
import {
  exportApiLogs,
  fetchApiLogs,
} from '../../requests/queries/Developers/apiLogs';
import {PageContext} from '../../helpers/context';
import {useAllApps} from '../../hooks/useApps';
import FilterV2 from '../../components/FilterV2';
import PageNumber from 'components/PageNumber';
import CardSummary from 'components/CardSummary';
import {dateRanges, formatDate} from 'helpers/getDateRange';

function DevelopersApiLogs({auth: {userDetails}}) {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [exportLogs, setExportLogs] = useState(false);
  const [isShowing, setIsShowing] = useState(false);
  const {setPageName} = useContext(PageContext);
  const {userApps} = useAllApps();

  useEffect(() => {
    setPageName('developers');
  }, [setPageName]);

  const apiParams = parseUrlParams();
  apiParams.limit = limit;
  apiParams.mode = apiParams.mode?.replace(/_/g, ' ') || null;

  const {data: filters, isLoading} = useQuery(['filters'], fetchApiLogsFilters);

  const {data: logs, isLoading: logsLoading} = useQuery(
    ['api-logs', {...apiParams}],
    () => fetchApiLogs(apiParams),
    {
      keepPreviousData: true,
    },
  );

  const {
    isLoading: exportLogsLoading,
    isFetching: exportLogsFetching,
    refetch,
  } = useQuery(
    ['api-logs', {...apiParams, exportLogs}],
    () => exportApiLogs(apiParams),
    {
      enabled: exportLogs,
    },
  );

  const handleExportLogs = useCallback(async () => {
    setExportLogs(true);
    try {
      const result = await refetch();
      toast.success(result?.data?.message || '');
      if (!exportLogsLoading || !exportLogsFetching) {
        setExportLogs(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [exportLogsLoading, exportLogsFetching, refetch]);

  const getAppName = useCallback(
    appId =>
      !isLoading && userApps && userApps?.find(app => app.value === appId),
    [isLoading, userApps],
  );

  const pageClick = selected => {
    setCurrentPage(selected);
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', selected);
    navigate(`?${searchParams.toString()}`);
  };

  useEffect(() => {
    if (!window.location.search) {
      const searchParams = new URLSearchParams();
      const {start, end} = dateRanges.seven_days();
      searchParams.set('date', 'seven_days');
      searchParams.set('start', start);
      searchParams.set('end', end);

      navigate(`?${searchParams.toString()}`, {replace: true});
    }
  }, []);

  const filterOptions = useMemo(() => {
    if (!isLoading && !filters) {
      return [];
    }

    const mapToOptionsArray = (items, valueKey = '_id') =>
      items?.map(item => ({
        value: item?.[valueKey] || item?.replace(/ /g, '_')?.toLowerCase(),
        label: getAppName(item)?.label || item,
      })) || [];

    return [
      ...(userApps?.length > 0
        ? [
            {
              key: 'apps',
              label: 'Apps',
              type: 'select',
              options: userApps,
              multiSelect: true,
            },
          ]
        : []),
      {
        key: 'mode',
        label: 'Mode',
        type: 'select',
        options: mapToOptionsArray(filters?.modes),
        multiSelect: false,
      },
      {
        key: 'status_code',
        label: 'Status Code',
        type: 'select',
        options: mapToOptionsArray(filters?.statusCodes),
        multiSelect: false,
      },
      {
        key: 'services',
        label: 'Services',
        type: 'select',
        options: mapToOptionsArray(filters?.services),
        multiSelect: true,
      },
    ];
  }, [filters, isLoading, userApps]);

  const handleFilterChange = newFilters => {
    const filteredOptions = filterOutEmptyValues(newFilters);

    if (filteredOptions.dateRange) {
      filteredOptions.start = formatDate(
        new Date(filteredOptions.dateRange.start),
      );
      filteredOptions.end = formatDate(new Date(filteredOptions.dateRange.end));
      delete filteredOptions.dateRange;
    }

    if (filteredOptions.apps) {
      filteredOptions.apps = Array.isArray(filteredOptions.apps)
        ? filteredOptions.apps.join(',')
        : filteredOptions.apps;
    }

    if (filteredOptions.services) {
      filteredOptions.services = Array.isArray(filteredOptions.services)
        ? filteredOptions.services.join(',')
        : filteredOptions.services;
    }

    setCurrentPage(1);
    const searchParams = new URLSearchParams();
    Object.entries(filteredOptions).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    navigate(`?${searchParams.toString()}`);
  };

  const isOwner = useMemo(
    () => userDetails.permissions.role === 'Owner',
    [userDetails.permissions.role],
  );

  const {viewAndExportLogs: canViewAndExportLogs} = useMemo(
    () => userDetails?.permissions?.developerPermissions || {},
    [userDetails],
  );

  const statusCodeMeanings = useMemo(
    () => [
      {code: 200, meaning: 'successful'},
      {code: 201, meaning: 'empty strings'},
      {code: 400, meaning: 'Bad Request'},
      {code: 401, meaning: 'unauthorized secret keys'},
      {code: 402, meaning: 'Low Balance'},
      {code: 424, meaning: 'Unable to reach service'},
      {code: 403, meaning: 'session not found'},
      {code: 404, meaning: 'not found'},
      {code: 405, meaning: 'method not allowed'},
      {code: 500, meaning: 'server error'},
      {code: 502, meaning: 'gateway timeout'},
    ],
    [],
  );

  const isFiltered =
    !logsLoading &&
    logs?.api_logs?.length === 0 &&
    Object.keys(apiParams).length > 2;

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">API Logs</p>
      }
    >
      <div className="flex items-center justify-end gap-2 flex-wrap py-2  mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SecondaryButton
          buttonText="Filter"
          onClick={() => setOpenFilter(prev => !prev)}
        />
        <PrimaryButton
          loading={exportLogsLoading || exportLogsFetching}
          onClick={handleExportLogs}
          buttonText="Export"
          style={{width: '80px'}}
          disabled={!canViewAndExportLogs}
        />
      </div>

      <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
        <FilterV2
          filters={filterOptions}
          onFilterChange={handleFilterChange}
          initialValues={apiParams}
        />
      </div>

      {(canViewAndExportLogs || isOwner) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-center gap-4 py-6">
          <CardSummary
            title="APIs Created"
            body={logs?.summary?.total || 0}
            loading={logsLoading}
          />
          <CardSummary
            title="Successful API Calls"
            body={logs?.summary?.successful || 0}
            loading={logsLoading}
          />
          <CardSummary
            title="Failed API Calls"
            body={logs?.summary?.failed || 0}
            loading={logsLoading}
          />
        </div>
      )}

      <div className="mb-5 flex justify-end w-full">
        <PopOver
          show={isShowing}
          header={
            <div
              onMouseEnter={() => setIsShowing(true)}
              onMouseLeave={() => setIsShowing(false)}
              className="flex items-center justify-center"
            >
              <h1 className="text-brandBlue mr-2 text-tiny">
                What do these codes mean?
              </h1>
              <img src={question} alt="question mark" />
            </div>
          }
        >
          <div>
            {statusCodeMeanings.map(({code, meaning}) => (
              <p
                key={meaning}
                className={`uppercase py-2 text-small ${
                  code.toString().includes('20')
                    ? 'text-success'
                    : code.toString().includes('50')
                    ? 'text-danger'
                    : 'text-grey80'
                }`}
              >{`${code} - ${meaning}`}</p>
            ))}
          </div>
        </PopOver>
      </div>

      <TableLayout negativeMargins>
        <thead className="font-semibold uppercase">
          <tr className="bg-white80">
            <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-end gap-1">
                <span>App Name</span>
              </div>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-end gap-1">
                <span>mode</span>
              </div>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-end gap-1">
                <span>Services</span>
              </div>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-end gap-1">
                <span>date/time</span>
              </div>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-end gap-1">
                <span>Status</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={8}>
              {logsLoading ? (
                <Loader height={45} />
              ) : !logsLoading &&
                (!canViewAndExportLogs || logs?.api_logs?.length === 0) ? (
                !isFiltered ? (
                  <EmptyState
                    body={
                      !canViewAndExportLogs
                        ? 'You do not have access to this resource'
                        : 'No API logs found.'
                    }
                    noBtn
                    height={45}
                  />
                ) : (
                  <EmptyState
                    body="No results found"
                    noBtn
                    customBtn={
                      <button
                        onClick={() => {
                          navigate('');
                          setCurrentPage(1);
                        }}
                        className="text-brandBlue p-4 text-sm font-medium"
                      >
                        Update preferences
                      </button>
                    }
                    height={45}
                  />
                )
              ) : null}
            </td>
          </tr>
        </tbody>
        <tbody className="text-sm bg-white">
          {logs?.api_logs &&
            logs?.api_logs?.map((log, i) => (
              <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                  {getAppName(log?.app)?.label || ['-']}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {log?.mode || 'API Call'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap capitalize">
                  {renderServices(log?.service) ||
                    log?.service?.replaceAll('_', ' ') ||
                    '-'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {format(new Date(log?.createdAt), 'do MMM, yyyy h:mm a')}
                </td>
                <td className="w-[17.6%] p-4 pl-0 text-tiny text-body whitespace-nowrap">
                  <div
                    className={`flex px-2 py-1 text-xs font-medium uppercase rounded-full cursor-pointer w-fit logs-code ${
                      errorCodeColor(log?.status_code)?.color
                    } ${errorCodeColor(log?.status_code)?.bg}`}
                  >
                    {log?.status_code || '-'}
                    <span className="before:content-['\00a0'] animate-dropdown">
                      - {log?.raw_status}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </TableLayout>

      {(canViewAndExportLogs || isOwner) &&
        !logsLoading &&
        logs?.totalRecords !== 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={+currentPage}
              limit={limit}
              totalCount={logs?.totalRecords || 0}
            />
            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={newLimit => {
                  setLimit(newLimit);
                  setCurrentPage(1);
                }}
                total={logs?.totalRecords}
                length={logs?.api_logs?.length}
                limit={limit}
              />
              <Pagination
                total={Math.ceil((logs?.totalRecords || 0) / limit)}
                current={+currentPage}
                onPageChange={pageClick}
              />
            </div>
          </div>
        )}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(DevelopersApiLogs);
